import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useGetStudentMarks, useGoToErrorPage } from 'core';
import { Colors, NetworkErrors, UserRole } from 'context';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { JournalPreloader } from 'atoms';
import { TaskResultsComponent } from './TaskResults';

interface ITaskResults {
  lessonsId: string;
  activeRole: UserRole;
}

export const TaskResults = memo(({ lessonsId, activeRole }: ITaskResults) => {
  const { t } = useTranslation();
  const { marks, loading } = useGetStudentMarks(lessonsId);
  const goToErrorPage = useGoToErrorPage();

  useEffect(() => {
    if (activeRole !== UserRole.STUDENT) {
      goToErrorPage(NetworkErrors.pageIsNotAvailable);
    }
  }, [goToErrorPage, marks, activeRole]);

  if (loading) {
    return <JournalPreloader />;
  }

  // "No marks" is an EMPTY state, not a loading one — the previous version fell
  // through to the preloader whenever `marks` was empty, so a lesson with
  // nothing checked yet span forever.
  return marks?.length ? (
    <TaskResultsComponent studentMarks={marks} />
  ) : (
    <EmptyText>{t('lesson.noTaskResults')}</EmptyText>
  );
});

const EmptyText = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;
